import React from 'react';
import { useAuth } from '../contexts/AuthContext';

const AlbumGrid = ({ albums, onAlbumClick, onDeleteAlbum, onEditAlbum }) => {
  const { isAuthorized } = useAuth();
  const formatDate = (dateString) => {
    // Add 'T00:00:00' to force local timezone interpretation instead of UTC
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper to get thumbnail URL from cover photo URL
  const getThumbnailUrl = (coverPhoto, albumId) => {
    if (!coverPhoto || !coverPhoto.url || !coverPhoto.name) return null;

    // Check if this is a video (has .mp4, .mov, etc extension)
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.m4v', '.mkv'];
    const isVideo = videoExtensions.some(ext => coverPhoto.name.toLowerCase().endsWith(ext));

    // Construct thumbnail path
    // For videos: albums/{albumId}/thumbs/{videoname}.jpg
    // For images: albums/{albumId}/thumbs/{imagename}
    const thumbName = isVideo ? `${coverPhoto.name}.jpg` : coverPhoto.name;
    const thumbPath = `albums/${albumId}/thumbs/${thumbName}`;

    // Replace the file path in the URL to point to thumbnail
    // Firebase Storage URLs look like: https://storage.googleapis.com/.../albums%2F{albumId}%2F{filename}
    const originalPath = `albums/${albumId}/${coverPhoto.name}`;
    const encodedOriginalPath = encodeURIComponent(originalPath);
    const encodedThumbPath = encodeURIComponent(thumbPath);

    return coverPhoto.url.replace(encodedOriginalPath, encodedThumbPath);
  };

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,440px))] gap-6 py-4 max-w-none mx-auto justify-center">
      {albums.map((album) => (
        <div key={album.id} className="relative bg-primary transition-all duration-200 overflow-hidden hover:-translate-y-0.5 group max-w-[440px]">
          <div className="cursor-pointer" onClick={() => onAlbumClick(album.id)}>
            <div className="flex items-center justify-center w-full h-0 pb-[66.67%] bg-gray-100 relative">
              {album.coverPhoto && album.coverPhoto.name !== 'bear.png' ? (
                <img
                  src={getThumbnailUrl(album.coverPhoto, album.id) || album.coverPhoto.url}
                  alt={album.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <img
                  src="/bear.png"
                  alt="Bear logo placeholder"
                  className="absolute inset-0 w-full h-full object-contain p-8 opacity-60"
                />
              )}
              {/* Lock icon for restricted albums */}
              {album.isRestricted && (
                <div className="absolute bottom-2 right-2 w-8 h-8 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <span className="material-icons text-white text-base">lock</span>
                </div>
              )}
            </div>
            <div className="text-center p-4">
              <h3 className="m-0 mb-2 text-lg font-semibold text-text-dark font-serif">{album.title}</h3>
              <p className="m-0 text-text-dark/70 text-sm font-sans">{formatDate(album.date)}</p>
            </div>
          </div>
          {isAuthorized && (
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                className="w-6 h-6 border-none bg-accent/80 rounded-full cursor-pointer flex items-center justify-center text-base text-text-light transition-all duration-200 opacity-0 group-hover:opacity-100 hover:bg-blue-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditAlbum(album);
                }}
                title="Edit Album"
              >
                <span className="material-icons text-base">edit</span>
              </button>
              <button
                className="w-6 h-6 border-none bg-accent/80 rounded-full cursor-pointer flex items-center justify-center text-base text-text-light transition-all duration-200 opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Delete album "${album.title}"?`)) {
                    onDeleteAlbum(album.id);
                  }
                }}
                title="Delete Album"
              >
                <span className="material-icons text-base">close</span>
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AlbumGrid;